import { ApiOutlined, CopyOutlined } from '@ant-design/icons';
import { AuthorizeResponse, IntegrationsResponse, type PlatformSetup, type ShopConnection } from '@serievo/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Card, Col, Row, Skeleton, Table, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { apiGet, apiPost } from '../api/client';
import { PlatformTag } from '../components/ui';
import { useWork } from '../features/context/WorkContext';

const REASON: Record<string, string> = {
  denied: 'Bạn đã từ chối hoặc hủy ủy quyền trên TikTok.',
  state: 'Phiên kết nối không hợp lệ hoặc đã hết hạn (10 phút). Vui lòng bấm kết nối lại.',
  forbidden: 'Tài khoản không còn quyền quản trị công ty này.',
  upstream: 'TikTok Shop không phản hồi hoặc từ chối mã ủy quyền. Vui lòng thử lại sau.',
  no_shop: 'Tài khoản TikTok này chưa có shop nào được ủy quyền.',
  owned_elsewhere: 'Shop này đã được kết nối ở một công ty khác trên SeriEvo.',
  not_configured: 'Máy chủ chưa cấu hình app TikTok Shop.',
};

const STATUS: Record<ShopConnection['status'], { label: string; color: string }> = {
  CONNECTED: { label: 'Đang kết nối', color: 'green' },
  REAUTH_REQUIRED: { label: 'Cần ủy quyền lại', color: 'orange' },
  REVOKED: { label: 'Đã thu hồi', color: 'default' },
};

const dt = (s: string | null) => (s ? new Date(s).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '—');

/** Kết nối shop với sàn: TikTok Shop qua OAuth (app Public "SeriEvo APP"), Shopee chờ partner_id. */
export function ConnectionsPage() {
  const w = useWork();
  const { message } = App.useApp();
  const [params] = useSearchParams();
  const result = params.get('tiktok');
  const q = useQuery({
    queryKey: ['integrations', w.tenant.id, w.entity.id],
    queryFn: ({ signal }) =>
      apiGet(`/integrations/shops?entity=${w.entity.id}`, IntegrationsResponse, { tenantId: w.tenant.id, signal }),
    select: (r) => r.data,
  });
  const connect = useMutation({
    mutationFn: () => apiPost('/integrations/tiktok/authorize', { entity: w.entity.id }, AuthorizeResponse, { tenantId: w.tenant.id }),
    onSuccess: (r) => window.location.assign(r.data.url),
    onError: (e) => void message.error(e.message),
  });

  const d = q.data;
  return (
    <div className="page">
      <Typography.Title level={4} className="page-title">
        Kết nối shop · {w.entity.shortName ?? w.entity.name}
      </Typography.Title>
      {result === 'ok' && (
        <Alert type="success" showIcon closable message={`Đã kết nối ${params.get('shops') ?? ''} shop TikTok. Token được lưu mã hóa trên máy chủ.`} />
      )}
      {result === 'error' && (
        <Alert type="error" showIcon closable message="Kết nối TikTok Shop chưa thành công" description={REASON[params.get('reason') ?? ''] ?? 'Lỗi không xác định.'} />
      )}
      {q.error && <Alert type="error" showIcon message={q.error.message} />}
      {!d ? (
        <Skeleton active />
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <PlatformCard
                title="TikTok Shop"
                platform="tiktok"
                setup={d.platforms.tiktok}
                action={
                  <Button type="primary" icon={<ApiOutlined />} disabled={!d.platforms.tiktok.canConnect} loading={connect.isPending} onClick={() => connect.mutate()}>
                    Kết nối shop TikTok
                  </Button>
                }
                note="Bạn sẽ được chuyển sang TikTok Shop để đăng nhập tài khoản seller và đồng ý cấp quyền cho app SeriEvo. SeriEvo chỉ đọc dữ liệu đơn hàng, quyết toán, không thay đổi gì trên shop."
              />
            </Col>
            <Col xs={24} md={12}>
              <PlatformCard
                title="Shopee"
                platform="shopee"
                setup={d.platforms.shopee}
                action={<Button disabled>Kết nối shop Shopee</Button>}
                note="Kết nối Shopee sẽ mở khi Shopee duyệt hồ sơ đối tác (ISV) và cấp partner_id cho SeriEvo."
              />
            </Col>
          </Row>
          <Card size="small" title="Shop đã kết nối">
            <Table<ShopConnection>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.connections}
              scroll={{ x: 720 }}
              locale={{ emptyText: 'Chưa có shop nào được kết nối với pháp nhân này' }}
              columns={[
                { title: 'Shop', key: 's', render: (_, r) => <><PlatformTag p={r.platform} /> {r.shopName}</> },
                { title: 'Mã shop', dataIndex: 'shopId', className: 'num' },
                { title: 'Khu vực', dataIndex: 'region', width: 80 },
                { title: 'Trạng thái', dataIndex: 'status', render: (s: ShopConnection['status']) => <Tag color={STATUS[s].color}>{STATUS[s].label}</Tag> },
                { title: 'Token hết hạn', dataIndex: 'accessExpiresAt', render: dt },
                { title: 'Kết nối lúc', dataIndex: 'connectedAt', render: dt },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}

function PlatformCard(p: { title: string; platform: 'tiktok' | 'shopee'; setup: PlatformSetup; action: ReactNode; note: string }) {
  const { message } = App.useApp();
  return (
    <Card size="small" title={<><PlatformTag p={p.platform} /> {p.title}</>} extra={<Tag color={p.setup.configured ? 'green' : 'default'}>{p.setup.configured ? 'Sẵn sàng' : 'Chưa mở'}</Tag>} style={{ height: '100%' }}>
      <Typography.Paragraph type="secondary">{p.note}</Typography.Paragraph>
      {p.action}
      {p.setup.reason && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          {p.setup.reason}
        </Typography.Paragraph>
      )}
      {p.setup.redirectUri && (
        <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
          Redirect URL khai báo ở Partner Center:{' '}
          <Typography.Text code copyable={{ icon: <CopyOutlined />, onCopy: () => void message.success('Đã chép') }}>
            {p.setup.redirectUri}
          </Typography.Text>
        </Typography.Paragraph>
      )}
    </Card>
  );
}
